import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Info, Loader2, Send, X } from "lucide-react";
import {
  PERSONAL_DATA_AGREEMENT,
  PRIVACY_POLICY,
  USER_AGREEMENT,
} from "./legalTexts";
import "./auth.css";

const LEGAL_VERSIONS = {
  personalData: "1.0",
  privacyPolicy: "1.0",
  userAgreement: "1.0",
};

const LegalModal = ({ isOpen, onClose, title, content }) => {
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Клавиатура: фокус на «Закрыть» при открытии, Esc закрывает,
  // после закрытия фокус возвращается на ссылку, открывшую окно.
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div
        className="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-modal-head">
          <h2 className="auth-modal-title" id="legal-modal-title">
            {title}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="auth-modal-close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>
        <div className="auth-modal-body" tabIndex={0}>
          {content}
        </div>
        <div className="auth-modal-foot">
          <button type="button" className="auth-submit" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    companyName: "",
    inn: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [telegramLink, setTelegramLink] = useState(null);
  const [loading, setLoading] = useState(false);

  const [consentGiven, setConsentGiven] = useState(false);
  const consentTimestampRef = useRef(null);

  const [activeModal, setActiveModal] = useState(null);

  const { register } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleConsentChange = (e) => {
    const checked = e.target.checked;
    setConsentGiven(checked);
    consentTimestampRef.current = checked ? new Date().toISOString() : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consentGiven) {
      setError(
        "Необходимо принять условия использования и дать согласие на обработку данных.",
      );
      return;
    }

    const tg = formData.telegramUsername.replace(/^@/, "");
    if (!tg) {
      setError("Введите ваш Telegram username.");
      return;
    }
    if (!/^[a-zA-Z0-9_]{5,32}$/.test(tg)) {
      setError(
        "Некорректный Telegram username. Только латинские буквы, цифры и _, длина 5–32 символа.",
      );
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const data = await register(
        formData.email,
        formData.password,
        formData.companyName,
        formData.inn,
        tg,
        true,
        {
          checkbox_checked_at: consentTimestampRef.current,
          personal_data_version: LEGAL_VERSIONS.personalData,
          privacy_policy_version: LEGAL_VERSIONS.privacyPolicy,
          user_agreement_version: LEGAL_VERSIONS.userAgreement,
        },
      );
      setSuccess(
        "Запрос на доступ отправлен! Когда администратор его одобрит, мы напишем вам на почту.",
      );
      setTelegramLink(data?.telegram_link || null);
    } catch (err) {
      setError(err.response?.data?.error || "Ошибка при регистрации");
    } finally {
      setLoading(false);
    }
  };

  const legalTexts = {
    personalData: PERSONAL_DATA_AGREEMENT,
    privacyPolicy: PRIVACY_POLICY,
    userAgreement: USER_AGREEMENT,
  };

  const modalTitle = {
    personalData: "Обработка персональных данных",
    privacyPolicy: "Политика конфиденциальности",
    userAgreement: "Пользовательское соглашение",
  };

  const telegramInvalid = !!error && error.includes("Telegram");

  return (
    <div className="auth-screen auth-screen--long">
      <LegalModal
        isOpen={!!activeModal}
        onClose={() => setActiveModal(null)}
        title={activeModal ? modalTitle[activeModal] : ""}
        content={activeModal ? legalTexts[activeModal] : ""}
      />

      <div className="auth-wrap auth-wrap--wide">
        <p className="auth-brand">PASSX</p>

        <div className="auth-panel">
          <header className="auth-head">
            <h1 className="auth-title">Запрос доступа</h1>
            <p className="auth-lead">
              Заполните данные компании. Администратор проверит заявку и
              откроет доступ.
            </p>
          </header>

          {success && (
            <div className="auth-alert auth-alert--ok" role="status">
              <CheckCircle2 size={20} aria-hidden="true" />
              <span>{success}</span>
            </div>
          )}

          {success && telegramLink && (
            <div className="auth-form-body">
              <p className="auth-lead">
                Хотите получить ответ и в Telegram? Откройте бота и нажмите
                «Запустить».
              </p>
              <a
                className="auth-submit"
                href={telegramLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Send size={20} aria-hidden="true" />
                Подключить Telegram
              </a>
            </div>
          )}

          {!success && (
            <form className="auth-form-body" onSubmit={handleSubmit}>
              <fieldset className="auth-section">
                <legend className="auth-legend">Компания</legend>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="reg-company">
                    Название компании
                  </label>
                  <input
                    id="reg-company"
                    className="auth-input"
                    name="companyName"
                    type="text"
                    autoComplete="organization"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="reg-inn">
                    ИНН
                  </label>
                  <input
                    id="reg-inn"
                    className="auth-input"
                    name="inn"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={formData.inn}
                    onChange={handleChange}
                    required
                  />
                </div>
              </fieldset>

              <fieldset className="auth-section">
                <legend className="auth-legend">Учётная запись</legend>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="reg-email">
                    Email
                  </label>
                  <input
                    id="reg-email"
                    className="auth-input"
                    name="email"
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="reg-password">
                    Пароль
                  </label>
                  <input
                    id="reg-password"
                    className="auth-input"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </div>
              </fieldset>

              <fieldset className="auth-section">
                <legend className="auth-legend">Связь</legend>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="reg-telegram">
                    Telegram username
                  </label>
                  <div className="auth-affix">
                    <span className="auth-affix-sign" aria-hidden="true">
                      @
                    </span>
                    <input
                      id="reg-telegram"
                      className="auth-input"
                      name="telegramUsername"
                      type="text"
                      placeholder="username"
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={formData.telegramUsername}
                      onChange={handleChange}
                      required
                      aria-invalid={telegramInvalid ? "true" : undefined}
                      aria-describedby={
                        telegramInvalid
                          ? "reg-telegram-hint reg-error"
                          : "reg-telegram-hint"
                      }
                    />
                  </div>
                  <span className="auth-hint" id="reg-telegram-hint">
                    Для связи при одобрении заявки. Найти можно в настройках
                    Telegram.
                  </span>
                </div>
              </fieldset>

              <fieldset className="auth-section">
                <legend className="auth-legend">Согласие</legend>
                <label className="auth-consent" htmlFor="reg-consent">
                  <input
                    id="reg-consent"
                    className="auth-check"
                    type="checkbox"
                    checked={consentGiven}
                    onChange={handleConsentChange}
                  />
                  <span className="auth-consent-text">
                    Я принимаю условия{" "}
                    <button
                      type="button"
                      className="auth-linkbtn"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveModal("userAgreement");
                      }}
                    >
                      пользовательского соглашения
                    </button>
                    , даю согласие на{" "}
                    <button
                      type="button"
                      className="auth-linkbtn"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveModal("personalData");
                      }}
                    >
                      обработку персональных данных
                    </button>{" "}
                    и принимаю{" "}
                    <button
                      type="button"
                      className="auth-linkbtn"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveModal("privacyPolicy");
                      }}
                    >
                      политику конфиденциальности
                    </button>
                    .
                  </span>
                </label>

                <div className="auth-chips" aria-label="Версии документов">
                  {[
                    { label: "Польз. соглашение", key: "userAgreement" },
                    { label: "Обраб. данных", key: "personalData" },
                    { label: "Политика конф.", key: "privacyPolicy" },
                  ].map(({ label, key }) => (
                    <span key={key} className="auth-chip">
                      {label}
                      <span className="auth-chip-ver">
                        v{LEGAL_VERSIONS[key]}
                      </span>
                    </span>
                  ))}
                </div>

                <div className="auth-note">
                  <Info size={18} aria-hidden="true" />
                  <p>
                    Компания подтверждает, что берёт у своих клиентов согласие
                    на обработку персональных данных, в котором указано, что
                    они согласны на передачу третьим лицам и на трансграничную
                    передачу их документов.
                  </p>
                </div>
              </fieldset>

              {error && (
                <div
                  className="auth-alert auth-alert--warn"
                  role="alert"
                  id="reg-error"
                >
                  <AlertCircle size={20} aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <button
                className="auth-submit"
                type="submit"
                disabled={loading || !consentGiven}
                aria-busy={loading ? "true" : undefined}
                aria-describedby={
                  !consentGiven ? "reg-submit-hint" : undefined
                }
              >
                {loading && (
                  <Loader2
                    size={20}
                    className="auth-spin"
                    aria-hidden="true"
                  />
                )}
                {loading ? "Отправка..." : "Запросить доступ"}
              </button>
              {!consentGiven && (
                <p className="auth-submit-hint" id="reg-submit-hint">
                  Отметьте согласие, чтобы отправить заявку
                </p>
              )}
            </form>
          )}

          <p className="auth-switch">
            Уже есть аккаунт?{" "}
            <Link className="auth-link" to="/login">
              Войти
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
